import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';

const MONTHS_IN_YEAR = 12;

const emiFor = (principal, annualRate, months) => {
  const r = annualRate / MONTHS_IN_YEAR / 100;
  return (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
};

export const BalanceTransferCalculator = () => {
  const [outstanding, setOutstanding] = useState(4000000);
  const [currentRate, setCurrentRate] = useState(10);
  const [newRate, setNewRate] = useState(8.5);
  const [remainingYears, setRemainingYears] = useState(15);
  const [processingFeePct, setProcessingFeePct] = useState(0.5);

  const [results, setResults] = useState({
    currentEmi: 0, newEmi: 0, monthlySaving: 0, totalSaving: 0, processingFee: 0, netSaving: 0
  });

  const calculate = useCallback(() => {
    const months = remainingYears * MONTHS_IN_YEAR;
    const currentEmi = emiFor(outstanding, currentRate, months);
    const newEmi = emiFor(outstanding, newRate, months);
    const monthlySaving = currentEmi - newEmi;
    const totalSaving = monthlySaving * months;
    const processingFee = outstanding * (processingFeePct / 100);
    setResults({
      currentEmi, newEmi, monthlySaving, totalSaving, processingFee,
      netSaving: totalSaving - processingFee
    });
  }, [outstanding, currentRate, newRate, remainingYears, processingFeePct]);

  useEffect(() => { calculate(); }, [calculate]);

  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(value);

  return (
    <section className="py-20 bg-white" data-testid="balance-transfer-calculator">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Your Existing Loan Details
              </CardTitle>
              <CardDescription>Enter your current home loan and the new offer details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Outstanding Loan Amount</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(outstanding)}</span>
                </div>
                <Slider value={[outstanding]} onValueChange={(v) => setOutstanding(v[0])} min={500000} max={50000000} step={100000} data-testid="bt-outstanding-slider" />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Current Interest Rate (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{currentRate.toFixed(2)}%</span>
                </div>
                <Slider value={[currentRate]} onValueChange={(v) => setCurrentRate(v[0])} min={7} max={16} step={0.05} data-testid="bt-current-rate-slider" />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">New Interest Rate (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{newRate.toFixed(2)}%</span>
                </div>
                <Slider value={[newRate]} onValueChange={(v) => setNewRate(v[0])} min={7} max={16} step={0.05} data-testid="bt-new-rate-slider" />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Remaining Tenure</Label>
                  <span className="text-xl font-bold text-emerald-600">{remainingYears} years</span>
                </div>
                <Slider value={[remainingYears]} onValueChange={(v) => setRemainingYears(v[0])} min={1} max={30} step={1} data-testid="bt-tenure-slider" />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Processing Fee (of loan)</Label>
                  <span className="text-xl font-bold text-emerald-600">{processingFeePct.toFixed(2)}%</span>
                </div>
                <Slider value={[processingFeePct]} onValueChange={(v) => setProcessingFeePct(v[0])} min={0} max={2} step={0.05} data-testid="bt-fee-slider" />
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle className="text-emerald-700">Your Balance Transfer Savings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white p-6 rounded-xl border-2 border-emerald-200 text-center">
                  <p className="text-sm text-slate-600 mb-2">Net Savings (after processing fee)</p>
                  <p className="text-4xl font-bold text-emerald-600" data-testid="bt-net-saving">{formatCurrency(results.netSaving)}</p>
                  <p className="text-sm text-slate-500 mt-1">over {remainingYears} years</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Current EMI</p>
                    <p className="text-xl font-bold text-slate-900" data-testid="bt-current-emi">{formatCurrency(results.currentEmi)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">New EMI</p>
                    <p className="text-xl font-bold text-emerald-600" data-testid="bt-new-emi">{formatCurrency(results.newEmi)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Monthly Saving</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(results.monthlySaving)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Processing Fee</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(results.processingFee)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="pt-6">
                <TrendingDown className="w-8 h-8 mb-2" />
                <p className="text-sm opacity-90 mb-1">Why transfer?</p>
                <p className="text-sm opacity-90">
                  Even a 1% lower rate on a large home loan can save you lakhs over the remaining tenure.
                  We help you transfer to a bank offering better rates with minimal paperwork.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
